import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormContext } from 'react-hook-form';
import { ChevronLeft, ChevronRight, Pencil, Send } from 'lucide-react';
import PerguntaUnicaEscolha from './PerguntaUnicaEscolha';
import PerguntaMultiplaEscolha from './PerguntaMultiplaEscolha';
import PerguntaAberta from './PerguntaAberta';

const COMPONENTE_POR_TIPO = {
  unica_escolha: PerguntaUnicaEscolha,
  multipla_escolha: PerguntaMultiplaEscolha,
  aberta: PerguntaAberta
};

// Tempo entre marcar a opção e o card voar sozinho — só existe pra pessoa
// VER a própria seleção destacada antes da tela mudar; sem esse respiro o
// clique e a troca de card aconteceriam juntos demais, parecendo bug.
const ATRASO_AUTO_AVANCO_MS = 450;

function respondida(pergunta, valor) {
  if (pergunta.tipo === 'unica_escolha') return !!valor;
  if (pergunta.tipo === 'multipla_escolha') return Array.isArray(valor) && valor.length > 0;
  return typeof valor === 'string' && valor.trim().length > 0;
}

function resumoResposta(pergunta, valor) {
  if (!respondida(pergunta, valor)) return null;
  if (pergunta.tipo === 'unica_escolha') {
    return pergunta.opcoes.find((o) => o.slug === valor)?.texto || valor;
  }
  if (pergunta.tipo === 'multipla_escolha') {
    return valor.map((slug) => pergunta.opcoes.find((o) => o.slug === slug)?.texto || slug).join(', ');
  }
  return valor;
}

// Um card de pergunta no fluxo de votação — diferente de CartaArrastavel
// (ResultadoEnqueteDinamica.js), aqui não tem gesto de arraste: o avanço é
// sempre programático (clique na opção de única escolha, ou botão "Próxima"
// nos outros dois tipos — arrastar pra "responder" não faz sentido num
// formulário de voto). `direcao` só decide o lado da animação (1 = avançou,
// voa/entra da direita; -1 = voltou ou pulou via "Editar" na revisão,
// voa/entra da esquerda), pra manter a sensação espacial coerente nos dois
// sentidos de navegação.
const CardPerguntaVoto = ({ pergunta, indice, total, direcao, avancar, voltar, primeira, corTexto }) => {
  const { watch } = useFormContext();
  const fieldName = `respostas.${pergunta.id}`;
  const valor = watch(fieldName);
  const Componente = COMPONENTE_POR_TIPO[pergunta.tipo];

  useEffect(() => {
    if (pergunta.tipo !== 'unica_escolha' || !valor) return undefined;
    const timer = setTimeout(avancar, ATRASO_AUTO_AVANCO_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  if (!Componente) return null;

  const ultima = indice + 1 === total;

  return (
    <motion.div
      initial={{ opacity: 0, x: direcao * -40, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ x: direcao * 500, opacity: 0, rotate: direcao * 8, transition: { duration: 0.3 } }}
      transition={{ type: 'spring', stiffness: 320, damping: 30 }}
    >
      <p className={`text-center text-sm font-medium mb-3 ${corTexto?.corpo || 'text-gray-400'}`}>
        Pergunta {indice + 1} de {total}
      </p>

      <Componente pergunta={pergunta} indice={indice + 1} />

      <div className="flex items-center justify-between mt-4">
        {!primeira ? (
          <button
            type="button"
            onClick={voltar}
            className={`inline-flex items-center gap-1 text-sm hover:text-primary-500 transition-colors ${corTexto?.corpo || 'text-gray-500'}`}
          >
            <ChevronLeft className="w-4 h-4" /> Voltar
          </button>
        ) : <span />}

        <button
          type="button"
          onClick={avancar}
          disabled={pergunta.obrigatoria && !respondida(pergunta, valor)}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>{ultima ? 'Revisar respostas' : 'Próxima'}</span> <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

// Orquestra o fluxo pergunta-a-pergunta: card atual em foco, os outros nem
// chegam a montar (diferente da pilha de resultado, que pré-monta até 3
// cards pra dar o efeito de profundidade — aqui não tem "cards atrás",
// então não tem por que pagar esse custo). react-hook-form v7 não
// desregistra valor de campo desmontado por padrão (shouldUnregister:false,
// configurado explicitamente lá em FormularioDinamico.js), então sair e
// voltar por um card já respondido preserva o que foi marcado.
const FluxoCartasPerguntas = ({ perguntas, corTexto, enviando }) => {
  const { getValues } = useFormContext();
  const [indice, setIndice] = useState(0);
  const [direcao, setDirecao] = useState(1);

  const avancar = () => {
    setDirecao(1);
    setIndice((i) => Math.min(i + 1, perguntas.length));
  };
  const voltar = () => {
    setDirecao(-1);
    setIndice((i) => Math.max(i - 1, 0));
  };
  const irPara = (i) => {
    setDirecao(-1);
    setIndice(i);
  };

  if (indice >= perguntas.length) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Revisar respostas</h3>
          <p className="text-sm text-gray-500 mb-4">Confira antes de enviar — dá pra editar qualquer uma.</p>

          <div className="space-y-4">
            {perguntas.map((pergunta, i) => {
              const valor = getValues(`respostas.${pergunta.id}`);
              const resumo = resumoResposta(pergunta, valor);
              return (
                <div key={pergunta.id} className="flex items-start justify-between gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-500">{i + 1}. {pergunta.texto}</p>
                    <p className={resumo ? 'text-base text-gray-900 font-medium' : 'text-base text-gray-400 italic'}>
                      {resumo || 'Não respondida'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => irPara(i)}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card text-center">
          <button
            type="submit"
            disabled={enviando}
            className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {enviando ? (
              <>
                <div className="loading-spinner" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Confirmar e enviar</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  const pergunta = perguntas[indice];

  return (
    <AnimatePresence mode="wait">
      <CardPerguntaVoto
        key={pergunta.id}
        pergunta={pergunta}
        indice={indice}
        total={perguntas.length}
        direcao={direcao}
        avancar={avancar}
        voltar={voltar}
        primeira={indice === 0}
        corTexto={corTexto}
      />
    </AnimatePresence>
  );
};

export default FluxoCartasPerguntas;
